import { GetStaticPathsResult, GetStaticPropsContext } from "next";
import Geocode from "react-geocode";
import useApi from "@/hooks/useApi";
import { initFilters } from "@/libs/constants";
import {
  MIN_PRICE,
  MAX_PRICE,
  MIN_YEAR,
  MAX_YEAR,
  MIN_MILES,
  MAX_MILES,
} from "@/libs/constants";
import _ from "lodash";
import { backOff } from "exponential-backoff";
import { googleGeocodeApiKey } from "../libs/clientConfig";
import {
  paServerStrapiAuth,
  paServerStrapiPreview,
  paServerStrapiUrl,
} from "../libs/serverApi";
import dynamic from "next/dynamic";
import { mapStrapiData } from "./components/vehicle-search";
import qs from "qs";

const PAGE_SIZE = 10;

const MAPPINGS = [
  ["", "base-pages"],
  ["car-info", "car-infos"],
  ["blog", "blogs"],
  ["buy", "buy-pages"],
  ["competitors", "competitors"],
  ["financing", "financings"],
  ["insurance", "insurances"],
  ["license-plates", "license-plates"],
  ["news", "news-pages"],
  ["paperwork", "paperworks"],
  ["sell", "sell-pages"],
  ["sales-taxes", "taxes"],
  ["title", "titles"],
  ["transport", "transports"],
  ["warranty", "warranties"],
  ["registration", "registrations"],
  ["payments", "payments"],
  ["bill-of-sale", "bill-of-sales"],
  ["photography", "photographies"],
  ["inspection", "inspections"],
  ["author", "authors"],
];

export type PageParams = {
  slug?: string[];
  path?: string;
  collection?: string;
  prefix?: string;
  redirect?: {
    source: string;
    destination: string;
    permanent: boolean;
  };
};
export type PageEntry = {
  params?: PageParams;
};

export type PageSet = {
  [slug: string]: PageEntry;
};

export interface PageSitemapMeta {
  slug: string[];
  path: string;
  updatedAt: string;
  collection: string;
}

export enum LoadStrategys {
  lazyOnLoad = "lazyOnLoad",
  afterInteractive = "afterInteractive",
  beforeInteractive = "beforeInteractive",
}

export function allCollections() {
  return MAPPINGS.map((m) => m[1]);
}

export function baseCollection() {
  return toCollection("");
}

export function redirects() {
  return "redirects";
}

export function toPrefix(collection: string) {
  const mp = MAPPINGS.find((m) => m[1] === collection);
  return mp ? mp[0] : undefined;
}
export function toCollection(prefix: string): string {
  const col = MAPPINGS.filter((m) => m[0] === prefix);
  if (col.length > 0) return col[0][1];
  return "";
}

const POPULATE_DEPTH = 7;
const MAX_ATTEMPTS = 3;
let drafts = false;

function setDrafts(useDrafts: boolean) {
  if (useDrafts != drafts) {
    drafts = useDrafts;
    console.log(useDrafts ? "" : "Not ", "Including DRAFTS in output");
  }
}

const strategyMap: Record<string, LoadStrategys> = {
  Immediate: LoadStrategys.beforeInteractive,
  AfterPageFinished: LoadStrategys.afterInteractive,
  WhenUserInteracts: LoadStrategys.lazyOnLoad,
};

export async function getSnippets(): Promise<any> {
  if (!isStrapiConfigured()) return { header: [], bodyTop: [], bodyBottom: [] };
  const { baseUrl, authorization, useDrafts } = getConfig();
  const HEADER_STRAPI_URL = new URL(
    `header-elements?populate=deep${
      useDrafts ? "&publicationState=preview" : ""
    }`,
    baseUrl
  );

  const header_res = await fetch(HEADER_STRAPI_URL, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: authorization,
    },
  });

  const hdrs = await header_res.json();
  const header: any[] = [];
  const bodyTop: any[] = [];
  const bodyBottom: any[] = [];

  if (hdrs?.data)
    hdrs.data
      .map((hdr: any) => {
        return hdr.attributes.Content.replaceAll(/<!--(.*?)-->/g, "")
          .split("<script")
          .map((s: string) => {
            let r = s.trim();
            const pos = r.indexOf(">");
            let attrs = r.slice(0, pos);
            const isAsync = attrs.match(/^async | async$/) !== null;
            if (isAsync) attrs = attrs.replace(/^async | async$/, "");
            let arr = attrs.match(/src *= *"(.*)"|src *= *'(.*)'/);
            const src =
              arr && arr.length > 0
                ? arr[0].replace(/src *= */, "").slice(1, -1)
                : null;

            let body = r.slice(pos + 1).trim();
            if (body.endsWith("</script>")) body = body.slice(0, -9);
            const strategy = strategyMap[hdr.attributes.LoadStrategy] || "";
            return {
              isAsync,
              src,
              body,
              place: hdr.attributes.Placement,
              strategy,
            };
          });
      })
      .flat()
      .filter(
        (s: any) =>
          (s.src && s.src.trim().length > 0) || (s.body && s.body.trim().length)
      )
      .forEach((h: any) => {
        const { place } = h;
        delete h.place;
        switch (place) {
          case "Body Top":
            bodyTop.push(h);
            break;

          case "Body Bottom":
            bodyBottom.push(h);
            break;

          case "Header":
          default:
            header.push(h);
        }
      });
  const snippets = {
    header,
    bodyTop,
    bodyBottom,
  };

  return snippets;
}

export async function getBanner(): Promise<any> {
  if (!isStrapiConfigured()) return {};
  const { baseUrl, authorization, useDrafts } = getConfig();
  const BANNER_STRAPI_URL = new URL(
    `banners?populate=deep${useDrafts ? "&publicationState=preview" : ""}`,
    baseUrl
  );
  const banner_res = await fetch(BANNER_STRAPI_URL, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: authorization,
    },
  });
  const banners = await banner_res.json();
  let displayBanner = {};
  banners.data.map((banner: any) => {
    if (banner.attributes.Live) {
      displayBanner = banner.attributes;
    }
  });
  return displayBanner;
}

export async function getAllRedirects(): Promise<PageEntry[]> {
  return strapiMapPages(
    async (
      item: any,
      collection: string,
      prefix: string
    ): Promise<any | null> => {
      const { RedirectFrom } = item.attributes;
      return {
        params: {
          slug: RedirectFrom.split("/").filter(
            (s: string) => s && s.length > 0
          ),
          path: RedirectFrom,
          collection,
          prefix,
        },
      };
    },
    [redirects()]
  );
}

let warnedUnconfigured = false;

// Strapi is optional at build time: CI builds run without SSM-derived env
// (getEnv.sh is not sourced in the workflow), and Strapi itself is mostly
// retired. When PA_STRAPI_URL is unset every entry point below degrades to
// empty content instead of failing the whole `next build`; pages regenerate
// via ISR once a configured runtime revalidates them.
export function isStrapiConfigured(): boolean {
  const configured = (paServerStrapiUrl ?? "") !== "";
  if (!configured && !warnedUnconfigured) {
    warnedUnconfigured = true;
    console.log(
      "PA_STRAPI_URL is not set - building without Strapi CMS content"
    );
  }
  return configured;
}

// How soon an unconfigured-build page asks ISR to try again.
const UNCONFIGURED_REVALIDATE_SECONDS = 60;

function getConfig() {
  const uri = paServerStrapiUrl ?? "";
  if (uri === "") throw new Error("Strapi base URL is not defined");

  return {
    baseUrl: uri.endsWith("/api/") ? uri : `${uri}/api/`,
    authorization: "Bearer " + paServerStrapiAuth,
    useDrafts: paServerStrapiPreview,
  };
}

function makeStrapiURL(collection: string, baseUrl: string, page: number = 0) {
  try {
    const u = new URL(
      `${collection}?populate=deep,${POPULATE_DEPTH}${
        drafts ? "&publicationState=preview" : ""
      }&pagination[page]=${page}&pagination[pageSize]=${PAGE_SIZE}`,
      baseUrl
    );
    // console.log(`makeStrapiURL: ${u}`);
    return u;
  } catch (e: any) {
    console.log(
      `makeStrapiURL(${collection},${baseUrl},${page}): ${e.message}`
    );
    throw e;
  }
}

async function strapiTryMapCollectionPage(
  col: string,
  perPage: (pageNo: any, col: string, prefix: string) => Promise<any>,
  pageNo: number = 0,
  attempt = 1
): Promise<any[]> {
  const { baseUrl, authorization } = getConfig();

  const endPoint: URL = makeStrapiURL(col, baseUrl, pageNo);
  const prefix = toPrefix(col) ?? col;

  try {
    // console.log(`endPoint: ${endPoint}`);
    const res = await fetch(endPoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    });
    const content = await res.json();
    const mapped = Array.isArray(content.data)
      ? content.data.map((item: any) => perPage(item, col, prefix))
      : [];
    console.log(`Strapi ${col}:${pageNo} try ${attempt} :  ${mapped.length}`);

    const settled = await Promise.allSettled(mapped);

    return settled
      .map((p) => (p.status === "fulfilled" ? p.value : undefined))
      .filter((v) => v !== null && v !== undefined);
  } catch (e: any) {
    console.log(`Strapi ${col}:${pageNo} try ${attempt} FAILED - ${e.message}`);
    throw e;
  }
}

async function strapiMapCollectionPage(
  col: string,
  perPage: (pageNo: any, col: string, prefix: string) => Promise<any>,
  pageNo: number = 0
): Promise<any[]> {
  const { baseUrl, authorization, useDrafts } = getConfig();

  setDrafts(useDrafts);

  const prefix = toPrefix(col) ?? col;
  if (prefix === undefined) {
    console.log(` 𐄂 [${col}] has no recognized "${prefix}" - skipping`);
    return [];
  }

  return await backOff(() => {
    return strapiTryMapCollectionPage(col, perPage, pageNo, 0);
  });
}

export async function strapiMapPages<T>(
  perPage: (page: any, collection: string, prefix: string) => Promise<T | null>,
  collections: string[] = allCollections(),
  options: any = {}
): Promise<T[]> {
  const mapped: T[] = [];

  const buildEmpty = options.buildEmpty ?? false;

  if (!buildEmpty && isStrapiConfigured()) {
    for (let idx in collections) {
      const collection = collections[idx];
      const prefix = toPrefix(collection) || collection;
      console.log(`[${collection} ==> ]`);

      let mp: any[] = [];
      let pageNo = 0;

      do {
        pageNo++;
        //console.log(`[ pg ${pageNo} >> ]`);
        //console.log(`Strapi ${collection}:${pageNo}`);
        mp = await strapiMapCollectionPage(collection, perPage, pageNo);
        mp.forEach((p) => mapped.push(p));
      } while (mp.length > 0);
    }
  }

  return mapped;
}

export async function getAuthorArticles(component: any, authorId: number) {
  if (!isStrapiConfigured()) return [];
  const { baseUrl, authorization, useDrafts } = getConfig();

  const queryParams = `?populate=deep,${POPULATE_DEPTH}${
    useDrafts ? "&publicationState=preview" : ""
  }`;

  const fetchPromises = MAPPINGS.map(([_, endpoint]) => {
    const STRAPI_URL = new URL(endpoint + queryParams, baseUrl);
    return fetch(STRAPI_URL.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    }).then((res) => res.json());
  });

  const allData = await Promise.all(fetchPromises);

  const resultArticles = allData.flatMap((data) =>
    data.data
      .filter(
        (article: any) => article.attributes.author?.data?.id === authorId
      )
      .map((article: any) => {
        const { attributes } = article;
        const authorInfo = attributes.author.data.attributes;

        return {
          slug: attributes.slug,
          title: attributes.PageTitle,
          publishedAt: attributes.publishedAt,
          author: authorInfo.AuthorName,
          authorPhoto: authorInfo.Headshot?.data?.attributes?.url || "",
          image: attributes.Content[1]?.Image?.data?.attributes?.url || "",
          articleContent: attributes.MetaDescription,
        };
      })
  );

  return resultArticles;
}

export async function getPaths(
  collection: string,
  fallback: boolean | "blocking" = "blocking",
  options: any = {}
): Promise<GetStaticPathsResult> {
  const buildEmpty = options.buildEmpty ?? false;

  if (!buildEmpty && isStrapiConfigured()) {
    const mapped: any[] = await strapiMapPages(
      async (item: any, collection: string) => {
        let { slug } = item.attributes;
        const prefix = toPrefix(collection) || collection;

        // let prefix = _.get(MAPPING, collection);
        // if (prefix && prefix.startsWith("/")) prefix = prefix.slice(1);

        slug = Array.isArray(slug) ? slug : slug.split(/,|\//);
        slug = slug.filter((s: string) => s.trim() !== "" && s.trim() != "/");
        const fslug = "/" + slug.join("/");
        /*
        if (fslug === "/") {
          slug = undefined;
        } else if (prefix !== undefined && (slug.length <= 1 || slug[0] !== prefix)) {
          slug = undefined;
          console.log(
            ` 𐄂 ${fslug} is in collection [${collection}] but is not prefixed with prefix "/${prefix}"`
          );
        } else {
          console.log(
            ` ✓ ${fslug} in collection [${collection}] with prefix "/${prefix}"`
          );
          //console.log(`${collection} : slug:  `, slug, ` => ${fslug} | `);
        }
        */
        const params = { params: { slug, path: fslug, collection } };
        return params;
      },
      [collection]
    );

    console.log(`Mapped: (${mapped.length})`);
    mapped.forEach((m) => console.log(JSON.stringify(m)));

    const paths: string[] = []; //(await Promise.all(await mapped)).filter(
    //  (s) => typeof s?.params?.slug === "string"
    //);

    return {
      paths,
      fallback,
    };
  }
  return {
    paths: [],
    fallback,
  };
}

export async function getProps(
  context: GetStaticPropsContext,
  collection: string,
  prefix: string | undefined = undefined,
  revalidate: number | boolean = false // = 30
): Promise<{ props: any; revalidate: number | boolean }> {
  if (!isStrapiConfigured()) {
    return {
      props: {
        content: null,
        headers: { header: [], bodyTop: [], bodyBottom: [] },
        banner: {},
      },
      // Retry via ISR so a configured runtime can fill the page in even
      // when the build ran without Strapi access.
      revalidate: UNCONFIGURED_REVALIDATE_SECONDS,
    };
  }
  const { baseUrl, authorization, useDrafts } = getConfig();

  let slug = context.params?.slug || [];
  slug = Array.isArray(slug) ? slug : slug.split("/");
  slug = slug.filter((s: string) => s.trim() !== "" && s.trim() != "/");
  if (prefix && slug.length > 0 && slug[0] !== prefix) slug.unshift(prefix);
  slug = "/" + slug.join("/");

  const searchOn = collection === "redirects" ? "RedirectFrom" : "slug";
  const STRAPI_URL = new URL(
    `${collection}?populate=deep,${POPULATE_DEPTH}${
      useDrafts ? "&publicationState=preview" : ""
    }&filters[${searchOn}][$eq]=${slug}`,
    baseUrl
  );

  try {
    const res = await fetch(STRAPI_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    });

    let content: any = null;

    const total_list = await res.json();
    if (Array.isArray(total_list.data) && total_list.data.length > 0) {
      content = total_list.data.find(
        (c: any) =>
          c?.attributes?.slug === slug || c?.attributes?.RedirectFrom === slug
      );

      if (content?.attributes?.RedirectTo) {
        const { RedirectFrom, RedirectTo, RedirectCode } = content.attributes;
        console.log(`Redirect from ${slug} to ${RedirectTo}`);
        return {
          props: {
            headers: {},
            slug,
            redirect: {
              source: RedirectFrom,
              destination: RedirectTo,
              permanent: RedirectCode === "Permanent (301)",
            },
          },
          revalidate,
        };
      }

      if (Array.isArray(content?.attributes?.Content)) {
        await Promise.all(
          content.attributes.Content.map(async (item: any) => {
            if (item.__component === "page-elements.author-blogs") {
              item.Articles = await getAuthorArticles(item, content?.id);
            }
            if (item.__component === "page-elements.link-hub") {
              const links2: any = [];
              const links = item?.LinkHub?.data?.attributes?.Pages || [];
              const waits = links.map(async (link: any) => {
                if (link.__component === "linkhub.blog-filter") {
                  const maxResults = link.MaxResults;
                  const vehicleTypes = (link.VehicleType?.data || []).map(
                    (s: any) => s?.attributes?.Label || ""
                  );
                  const states = (link.States?.data || []).map(
                    (s: any) => s?.attributes?.Name || ""
                  );
                  const blogsUrl = new URL(
                    `blogs?populate=deep,${POPULATE_DEPTH}${
                      useDrafts ? "&publicationState=preview" : ""
                    }`,
                    baseUrl
                  );
                  try {
                    const res = await fetch(blogsUrl, {
                      method: "GET",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                      },
                    });
                    const blogs = await res.json();
                    if (Array.isArray(blogs.data) && blogs.data.length > 0) {
                      blogs.data.forEach((blog: any) => {
                        const bTypes = (
                          blog?.attributes?.VehicleTypes?.data || []
                        ).map((s: any) => s?.attributes?.Label || "");
                        const bStates = (
                          blog?.attributes?.States?.data || []
                        ).map((s: any) => s?.attributes?.Name || "");
                        if (
                          bTypes.some((s: string) =>
                            vehicleTypes.includes(s)
                          ) &&
                          bStates.some((t: string) => states.includes(t)) &&
                          links2.length < maxResults
                        ) {
                          links2.push({
                            id: links2.length + 1,
                            __component: "linkhub.blog-link",
                            Title: blog.attributes.PageTitle,
                            link: {
                              data: blog,
                            },
                          });
                        }
                      });
                    }
                  } catch (e: any) {
                    console.log(
                      `getProps - Strapi content for ${blogsUrl} (${authorization}) - ${e}`
                    );
                  }
                } else {
                  link.id = links2.length + 1;
                  links2.push(link);
                }
              });
              await Promise.allSettled(waits);
              if (item?.LinkHub?.data?.attributes?.Pages)
                item.LinkHub.data.attributes.Pages = links2;
            }
          })
        );

        const enhancedContent = await Promise.all(
          content.attributes.Content.map(async (item: any) => {
            if (item.__component === "app-forms.vehicle-search") {
              const vehicleData = await mapStrapiData(item);
              const newItem = { ...item, ...(vehicleData ? vehicleData : {}) };
              return newItem;
            }
            return item;
          })
        );

        content.attributes.Content = enhancedContent;

        const headers = await getSnippets();
        const banner = await getBanner();

        return {
          props: {
            content,
            headers,
            banner,
            slug,
          },
          revalidate,
        };
      } else {
        console.log(`===>> ${collection} :: ${slug} -- NO content`);
      }
    }
    // else console.log(` 𐄂 No content in ${collection} found for ${slug} => ${slug}`);
  } catch (e: any) {
    console.log(
      `getProps - Strapi content for ${STRAPI_URL} (${authorization}) - ${e}`
    );
  }
  return {
    props: {
      content: null,
      headers: await getSnippets(),
      banner: await getBanner(),
    },
    revalidate,
  };
}

async function fetchStrapiData(
  collection: string,
  query: Record<string, unknown>
) {
  const { baseUrl, authorization, useDrafts } = getConfig();
  const params = {
    ...query,
    ...(useDrafts && { publicationState: "preview" }),
  };
  const url = new URL(`${collection}?${qs.stringify(params)}`, baseUrl);
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: authorization,
    },
  });
  return await response.json();
}

async function fetchAllCollectionData(collection: string, fields: string[]) {
  const firstPage = await fetchStrapiData(collection, {
    fields,
  });
  const { total, pageSize } = firstPage.meta.pagination;
  const remaining = total / pageSize - 1;
  const pages = Array.from({ length: remaining }, (_, i) =>
    fetchStrapiData(collection, {
      fields,
      pagination: {
        page: i + 2,
        pageSize,
      },
    })
  );
  const responses = await Promise.allSettled(pages);
  return [
    firstPage,
    ...responses
      .filter((e) => e.status === "fulfilled")
      .map((e: any) => e.value),
  ].reduce((acc, el) => [...acc, ...el.data], []);
}

export async function getStrapiCollectionSitemap(collection: string) {
  if (!isStrapiConfigured()) return [];
  const [rawRedirects, collectionPages] = await Promise.all([
    fetchAllCollectionData("redirects", ["RedirectFrom", "RedirectTo"]),
    fetchAllCollectionData(collection, ["slug", "updatedAt"]),
  ]);
  const redirectPages = rawRedirects.map((item: any) => {
    const { RedirectFrom } = item.attributes;
    return {
      params: {
        slug: RedirectFrom.split("/").filter((s: string) => s && s.length > 0),
        path: RedirectFrom,
        collection,
      },
    };
  });
  const prefix = toPrefix(collection) ?? collection;
  const fpaths = collectionPages.map((item: any) => {
    let { slug } = item.attributes;
    if (!slug) {
      return null;
    }
    slug = Array.isArray(slug) ? slug : slug.split(/,|\//);
    slug = slug.filter((s: string) => s && s.trim() !== "");
    const fslug = "/" + slug.join("/");
    if (prefix.length > 0 && slug[0] !== prefix) {
      process.stdout.write(
        `\n 𐄂 ${fslug} in [${collection}] without prefix "${prefix}" - ${JSON.stringify(
          slug
        )}`
      );
      return null;
    }
    return {
      slug,
      path: fslug,
      updatedAt: item.attributes.updatedAt,
      collection,
    };
  });
  const redirects: { [slug: string]: PageEntry } = {};
  redirectPages.forEach((r: any) => {
    const key: string = r.params?.redirect?.source ?? "";
    if (key !== "") {
      if (redirects[key]) {
        process.stdout.write(
          `\n---- Duplicate redirect [${key}] to [${r.params?.redirect?.destination}];`
        );
      }
      redirects[key] = r;
    }
  });
  const reserved: string[] = ["/"];
  const mpaths: { [slug: string]: PageSitemapMeta } = {};

  // process the pages
  fpaths.filter(Boolean).forEach((p: any) => {
    if (p?.slug && p?.path) {
      const key: string = p.path;
      if (reserved.includes(key)) {
        process.stdout.write(
          `\n---- Reserved slug [${p.path}] in [${p.collection}]...`
        );
      } else if (redirects[key]) {
        process.stdout.write(
          `\n---- Redirected slug [${p.path}] in [${p.collection}] will be discarded;`
        );
      } else if (mpaths[key]) {
        process.stdout.write(
          `\n---- Duplicate slug [${p.path}] in [${p.collection}] will be discarded; already exists in  [${mpaths[key]?.collection}]...`
        );
      } else {
        mpaths[key] = p;
      }

      if (key.startsWith("//")) console.log(`double slash: ${key}`);
    }
  });
  return Object.values(mpaths);
}
